import {
  throttle,
  put,
  select,
  all,
  fork,
  spawn,
  call,
  take,
} from 'redux-saga/effects';
import {
  getAllProjects,
  getContactOffset,
  getCompanyOffset,
  getContactLastSync,
  getCompanyLastSync,
  getHasCompletedRegistration,
  getProjectsIsAllFetching,
  getProjectsLastFetch,
  getActiveProjectTabView,
} from '../apps/main/rootReducer';
import {
  FETCH_CONTACTS_ME_ROLLBACK,
  SYNC_CONTACTS_ME,
  fetchContactsOffset,
} from '../modules/contacts/actions';
import { SYNC_COMPANIES, fetchCompanies } from '../modules/companies/actions';
import moment from 'moment';
import {
  ActiveProjectTabViewAction,
  FETCH_PROJECT_NEWS,
  fetchProjectNews,
  fetchProjectNewsCounts,
  fetchProjectsOverview,
  SET_ACTIVE_PROJECT_TAB_VIEW,
  SYNC_GLOBAL_PROJECTS,
  SYNC_GLOBAL_PROJECTS_FORCE,
} from '../modules/projects/actions';
import { notification } from 'antd';
import i18n from '../i18n';
import { SYNC_ABSENCES_ME, fetchAbsencesMe } from '../modules/absences/actions';
import { ReduxAction } from '../models/Redux';
import { DateTimeString } from '../models/Types';
import {
  SYNC_TIMERECORDS_ME,
  fetchMyTimeRecords,
} from '../modules/timeRecords/actions';
import {
  SYNC_MONTHLY_CLOSE_ME,
  fetchMonthlyCloseMe,
} from '../modules/timeKeeping/actions';
import { dispatchSaga } from '../util/sagas';
import { shouldRefreshCache } from '../util/helpers';
import {
  FETCH_USER_REGISTRATION_STATE_COMMIT,
  fetchUserRegistrationState,
  SYNC_USER_REGISTRATION_STATE,
} from '../modules/users/actions/registrationState';
import { LOGGED_IN } from '../modules/auth/actions';

function* syncContactsMe() {
  try {
    const offset = yield select(getContactOffset);
    const lastSync = yield select(getContactLastSync);
    const today = moment().toISOString().slice(0, 23);
    const refresh: boolean = moment(lastSync).diff(moment(today), 'days') > 3;

    if (refresh || !offset) {
      yield call(dispatchSaga, fetchContactsOffset());
    } else if (offset) {
      yield call(dispatchSaga, fetchContactsOffset(offset));
    }
  } catch (error) {
    console.error('Error in syncContactsMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('contacts:errorMessages.fetchError'),
    });
    yield put({
      type: FETCH_CONTACTS_ME_ROLLBACK,
    });
  }
}
function* syncCompanies() {
  try {
    const offset = yield select(getCompanyOffset);
    const lastSync = yield select(getCompanyLastSync);
    const today = moment().toISOString();
    const refresh = moment(today).diff(moment(lastSync), 'days') > 3;
    const validOffset = offset?.replace(/:(\d{3})$/, '.$1Z');
    yield call(dispatchSaga, fetchCompanies(refresh ? null : validOffset));
  } catch (error) {
    console.error('Error in syncCompanies', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('companies:errorMessages.fetchError'),
    });
  }
}
function* performProjectsSync(force: boolean = false): any {
  try {
    const allProjects = yield select(getAllProjects);
    const projectsIsFetching = yield select(getProjectsIsAllFetching);
    const lastFetch = yield select(getProjectsLastFetch);

    const cacheEmpty = !Array.isArray(allProjects) || allProjects.length === 0;
    const shouldUpdate = shouldRefreshCache(lastFetch, 5);
    const shouldFetch =
      force || (!projectsIsFetching && (cacheEmpty || shouldUpdate));

    if (shouldFetch) {
      yield put(fetchProjectsOverview());
    }
  } catch (error) {
    console.error('Error in performProjectsSync', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('projects:errorMessages.fetchError'),
    });
  }
}

function* syncGlobalProjects(): any {
  yield* performProjectsSync(false);
}

function* syncGlobalProjectsForce(): any {
  yield* performProjectsSync(true);
}
function* syncAbsencesMe(
  action: ReduxAction<{
    from?: DateTimeString;
    to?: DateTimeString;
  }>
) {
  try {
    const {
      meta: { from, to },
    } = action;
    yield spawn(dispatchSaga, fetchAbsencesMe(from, to));
  } catch (error) {
    console.error('Error in syncAbsencesMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('absences:errorMessages.fetchOverviewError'),
    });
  }
}
function* syncTimeRecordsMe(
  action: ReduxAction<{
    startDate?: DateTimeString;
    endDate?: DateTimeString;
  }>
) {
  try {
    const {
      meta: { startDate, endDate },
    } = action;
    yield spawn(dispatchSaga, fetchMyTimeRecords(startDate, endDate));
  } catch (error) {
    console.error('Error in syncTimeRecordsMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('timeRecords:errorMessages.fetchError'),
    });
  }
}
function* syncMonthlyCloseMe(
  action: ReduxAction<{
    from?: DateTimeString;
    to?: DateTimeString;
  }>
) {
  try {
    const {
      meta: { from, to },
    } = action;
    yield spawn(dispatchSaga, fetchMonthlyCloseMe(from, to));
  } catch (error) {
    console.error('Error in syncMonthlyCloseMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('timeKeeping:messages.errorMessages.fetchError'),
    });
  }
}

function* syncProjectNews() {
  try {
    yield put(fetchProjectNews());
    yield put(fetchProjectNewsCounts());
  } catch (error) {
    console.error('Error in watchFetchProjectNews', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('projects:errorMessages.fetchError'),
    });
  }
}

function* syncUserRegistrationState() {
  try {
    const hasCompletedRegistration = yield select(getHasCompletedRegistration);
    if (!hasCompletedRegistration) {
      yield put(fetchUserRegistrationState());
    } else {
      yield put({ type: FETCH_USER_REGISTRATION_STATE_COMMIT });
    }
  } catch (error) {
    console.error('Error in syncUserRegistrationState', error);
  }
}

function* watchSyncContacts() {
  yield throttle(60000, SYNC_CONTACTS_ME, syncContactsMe);
}
function* watchSyncCompanies() {
  yield throttle(60000, SYNC_COMPANIES, syncCompanies);
}
function* watchSyncGlobalProjects() {
  yield throttle(300000, SYNC_GLOBAL_PROJECTS, syncGlobalProjects);
}
function* watchSyncGlobalProjectsForce() {
  yield throttle(1000, SYNC_GLOBAL_PROJECTS_FORCE, syncGlobalProjectsForce);
}
function* watchSyncAbsencesMe() {
  yield throttle(60000, SYNC_ABSENCES_ME, syncAbsencesMe);
}
function* watchSyncTimerecordsMe() {
  yield throttle(60000, SYNC_TIMERECORDS_ME, syncTimeRecordsMe);
}
function* watchSyncMonthlyCloseMe() {
  yield throttle(60000, SYNC_MONTHLY_CLOSE_ME, syncMonthlyCloseMe);
}

function* watchFetchProjectNews() {
  yield throttle(60000, FETCH_PROJECT_NEWS, syncProjectNews);
}

function* watchSyncUserRegistrationState() {
  yield take(SYNC_USER_REGISTRATION_STATE);
  yield call(syncUserRegistrationState);
}

function* watchSyncProjectTabs() {
  yield take(LOGGED_IN);
  const activeTabView = yield select(getActiveProjectTabView);
  if (activeTabView === 'all') {
    yield call(syncGlobalProjectsForce);
  }
  yield throttle(
    20000,
    (action: ActiveProjectTabViewAction) => {
      if (action.type === SET_ACTIVE_PROJECT_TAB_VIEW) {
        return action.activeTab === 'all';
      }
      return false;
    },
    syncGlobalProjectsForce
  );
}

export default function* watchSyncActions() {
  yield all([
    fork(watchSyncUserRegistrationState),
    fork(watchSyncContacts),
    fork(watchSyncCompanies),
    fork(watchSyncGlobalProjects),
    fork(watchSyncGlobalProjectsForce),
    fork(watchSyncAbsencesMe),
    fork(watchSyncTimerecordsMe),
    fork(watchSyncMonthlyCloseMe),
    fork(watchFetchProjectNews),
    fork(watchSyncProjectTabs),
  ]);
}
